import React, { useMemo } from 'react';
import { Box, Typography, Iconography } from '@lucid/lego';
import { useStore } from '../../../store/store';
import claimsData from '../../../data/claimsCenter/claims.json';
import styles from './Analytics.module.scss';

interface Claim {
  id: string;
  claimId: string;
  vin: string;
  lucidId: string;
  claimType: string;
  submissionDate: string;
  status: string;
  amount: string;
  reviewer: string;
}

interface Metric {
  id: string;
  title: string;
  value: string;
  trend: {
    direction: 'up' | 'down';
    percentage: string;
    label: string;
  };
  icon: {
    category: string;
    name: string;
  };
  color: 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'orange' | 'teal' | 'magenta';
}

const Analytics: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const claims = claimsData.data as Claim[];

  const metrics = useMemo((): Metric[] => {
    const totalClaims = claims.length;
    const approvedClaims = claims.filter((c) => c.status === 'approved').length;
    const rejectedClaims = claims.filter((c) => c.status === 'rejected').length;
    const underReviewClaims = claims.filter((c) => c.status === 'under-review').length;
    const escalatedClaims = claims.filter((c) => c.status === 'info-required').length;
    const approvalRate =
      totalClaims > 0 ? ((approvedClaims / totalClaims) * 100).toFixed(1) : '0.0';

    // Calculate average resolution time (mock data for now)
    const avgResolutionDays = 5.6;

    // Calculate pending SLA breaches (claims under review for more than 7 days)
    const pendingSLABreaches = claims.filter((c) => {
      if (c.status !== 'under-review') return false;
      const daysDiff = Math.floor(
        (new Date().getTime() - new Date(c.submissionDate).getTime()) / (1000 * 60 * 60 * 24)
      );
      return daysDiff > 7;
    }).length;

    return [
      {
        id: 'total-claims',
        title: 'Total Claims Submitted',
        value: totalClaims.toLocaleString(),
        trend: { direction: 'up', percentage: '+12.5%', label: 'from last month' },
        icon: { category: 'global', name: 'document' },
        color: 'blue',
      },
      {
        id: 'approved-claims',
        title: 'Claims Approved',
        value: approvedClaims.toLocaleString(),
        trend: { direction: 'up', percentage: '+8.2%', label: 'from last month' },
        icon: { category: 'global', name: 'success' },
        color: 'green',
      },
      {
        id: 'rejected-claims',
        title: 'Claims Rejected',
        value: rejectedClaims.toLocaleString(),
        trend: { direction: 'up', percentage: '+3.1%', label: 'from last month' },
        icon: { category: 'global', name: 'close' },
        color: 'red',
      },
      {
        id: 'under-review',
        title: 'Claims Under Review',
        value: underReviewClaims.toLocaleString(),
        trend: { direction: 'down', percentage: '-2.4%', label: 'from last month' },
        icon: { category: 'global', name: 'time' },
        color: 'yellow',
      },
      {
        id: 'avg-resolution',
        title: 'Avg. Resolution Time',
        value: `${avgResolutionDays} days`,
        trend: { direction: 'down', percentage: '-0.8 days', label: 'improvement' },
        icon: { category: 'global', name: 'chart-line' },
        color: 'purple',
      },
      {
        id: 'escalated-claims',
        title: 'Escalated Claims',
        value: escalatedClaims.toLocaleString(),
        trend: { direction: 'up', percentage: '+5.2%', label: 'from last month' },
        icon: { category: 'global', name: 'alert' },
        color: 'orange',
      },
      {
        id: 'approval-rate',
        title: 'Claim Approval Rate',
        value: `${approvalRate}%`,
        trend: { direction: 'up', percentage: '+2.1%', label: 'from last month' },
        icon: { category: 'global', name: 'target' },
        color: 'teal',
      },
      {
        id: 'pending-sla',
        title: 'Pending SLA Breaches',
        value: pendingSLABreaches.toLocaleString(),
        trend: { direction: 'up', percentage: '+12.3%', label: 'needs attention' },
        icon: { category: 'global', name: 'time' },
        color: 'magenta',
      },
    ];
  }, [claims]);

  return (
    <Box>
      <Box pb={24}>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          Claims Analytics
        </Typography>
      </Box>
      <Box className={styles.metricsGrid}>
        {metrics.map((metric) => (
          <Box key={metric.id} className={`${styles.metricCard} ${styles[metric.color]}`}>
            <Box className={styles.cardHeader}>
              <Typography variant="body" size="small" color="white" theme={theme}>
                {metric.title}
              </Typography>
            </Box>
            <Box className={styles.cardBody}>
              <Typography variant="headline" size="medium" color="white" theme={theme}>
                {metric.value}
              </Typography>
              <Box className={styles.trend}>
                <Iconography
                  category="arrows"
                  name={metric.trend.direction === 'up' ? 'arrow-up' : 'arrow-down'}
                  color="white"
                  theme={theme}
                />
                <Typography variant="body" size="extraSmall" color="white" theme={theme}>
                  {metric.trend.percentage} {metric.trend.label}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Analytics;
